import { IkeToken, TimingEventAction, TimingEventType } from '@ikegps/core-api';
import { IkeDraw, IkeDrawState, IkeDrawTool, type SVGRenderOptions } from '@ikegps/core-shared';
import { runInAction } from 'mobx';
import { DrawButton } from '../../components/button/draw.button';
import { MaterialIcon } from '../../components/icon/icons';
import { ModalService } from '../../components/modal/modal.service';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { CSS, Style } from './draw.toolbar.style';

addStyles(Style);

function trackOnClick(draw: IkeDraw, action: string, value: number | null, cb: () => void) {
	return () => {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			action,
			draw.collection.revision,
			value ?? undefined
		);
		cb();
	};
}

export const DrawToolbar = {
	viewInsightGroup(draw: IkeDraw, ctx: SVGRenderOptions) {
		const isIkeAnalyst = Services.User.isIkeAnalyst(Services.User.current);

		// If the image is not markable, then don't show tools
		if (!draw.isMarkable) {
			return null;
		}

		const isInsightVisible = Services.Token.has(IkeToken.FEATURE_INSIGHT_AHOA) || isIkeAnalyst;

		const isInsightSparkleDisabled = !draw.drawState.hasInsightDetections();

		const isInsightMasksDisabled = !draw.drawState.hasVisibleInsightMasks(ctx.insightMaskTypesVisible ?? [], draw.log);

		const arrangeEnabled = Services.Token.has(IkeToken.FEATURE_ANNOTATIONS_ARRANGE) || isIkeAnalyst;

		return m('div', { className: CSS.group }, [
			...((isInsightVisible && [
				DrawButton.view(MaterialIcon.SPARKLE, {
					disabled: isInsightSparkleDisabled,
					isSelectedInsight: draw.state.visibleInsightMarkers,
					title: isInsightSparkleDisabled
						? T.TOOL_INSIGHT_DETECTIONS_MARKERS_NONE
						: T.TOOL_INSIGHT_DETECTIONS_TOGGLE_MARKERS,
					onclick: trackOnClick(draw, `insightDetections:markers:toggle:`, null, () =>
						draw.state.setInsightMarkersVisible(!draw.state.visibleInsightMarkers)
					),
				}),
				DrawButton.view(MaterialIcon.MASK_BLUR, {
					disabled: isInsightMasksDisabled,
					isSelectedInsight: draw.state.visibleInsightMasks,
					title: isInsightMasksDisabled ? T.TOOL_INSIGHT_DETECTIONS_MASKS_NONE : T.TOOL_INSIGHT_DETECTIONS_TOGGLE_MASKS,
					onclick: trackOnClick(draw, 'insightDetections:masks:toggle:', null, () =>
						draw.state.setInsightMasksVisible(!draw.state.visibleInsightMasks)
					),
				}),
			]) ||
				[]),
			...((arrangeEnabled && [
				DrawButton.view(MaterialIcon.VERTICAL_DISTRIBUTE, {
					title: T.TOOL_ARRANGE_ANNOTATIONS,
					isSelectedInsight: draw.state.autoArrangeAnnotations,
					onclick: trackOnClick(draw, 'autoArrangeAnnotations:toggle', null, () =>
						draw.state.setAutoArrangeAnnotations(!draw.state.autoArrangeAnnotations, ctx)
					),
				}),
			]) ||
				[]),
		]);
	},

	viewToolGroup(draw: IkeDraw): MithrilVNode {
		if (!draw.isMarkable) {
			return null;
		}

		if (draw.state.tool === IkeDrawTool.BASE) {
			return m('div', { className: CSS.group }, [
				DrawButton.view(MaterialIcon.POLE_BASE, {
					title: T.TOOL_BASE,
					selected: true,
					onclick: () => {
						Services.Analytics.trackTiming(
							TimingEventType.HOA,
							TimingEventAction.ENTER,
							draw.collection.id,
							new Date()
						);
						trackOnClick(draw, 'tool:base', null, () => false)();
					},
				}),
			]);
		}

		return m('div', { className: CSS.group }, [
			DrawButton.view(MaterialIcon.PHOTO_MEASURE, {
				flip: true,
				title: T.TOOL_HEIGHT,
				selected: draw.state.tool === IkeDrawTool.MARKER,
				onclick: () => {
					Services.Analytics.trackTiming(TimingEventType.HOA, TimingEventAction.ENTER, draw.collection.id, new Date());
					trackOnClick(draw, 'tool:marker', null, () => draw.state.toggleTool(IkeDrawTool.MARKER))();
				},
			}),
			DrawButton.view(MaterialIcon.PHOTO_MEASURE, {
				title: T.TOOL_WIDTH,
				selected: draw.state.tool === IkeDrawTool.LINE,
				onclick: trackOnClick(draw, 'tool:line', null, () => draw.state.toggleTool(IkeDrawTool.LINE)),
			}),
		]);
	},

	viewWireGroup(draw: IkeDraw, isZoomRendererShown: boolean): MithrilVNode {
		const isWireMeasurable = Services.Token.has(IkeToken.FEATURE_WIRE_MEASURE);

		if (isZoomRendererShown && isWireMeasurable) {
			return m('div', { className: CSS.group }, [
				DrawButton.view(MaterialIcon.ADD, {
					title: T.TOOL_CABLE_MEASURE_INCREASE,
					hidden: !isZoomRendererShown || !draw.state.showCablemeasure,
					disabled: !draw.state.canIncreaseCablemeasure,
					onclick: trackOnClick(draw, 'cablemeasure:up', draw.state.cablemeasure, () =>
						draw.state.cablemeasureIncrease()
					),
				}),
				DrawButton.view(MaterialIcon.SQUARE_FOOT, {
					title: T.TOOL_CABLE_MEASURE,
					hidden: !isZoomRendererShown,
					selected: draw.state.showCablemeasure,
					onclick: trackOnClick(draw, 'cablemeasure:toggle', draw.state.cablemeasure, () =>
						draw.state.toggleCablemeasure()
					),
				}),
				DrawButton.view(MaterialIcon.REMOVE, {
					title: T.TOOL_CABLE_MEASURE_DECREASE,
					hidden: !isZoomRendererShown || !draw.state.showCablemeasure,
					disabled: !draw.state.canDecreaseCablemeasure,
					onclick: trackOnClick(draw, 'cablemeasure:down', draw.state.cablemeasure, () =>
						draw.state.cablemeasureDecrease()
					),
				}),
			]);
		}

		return null;
	},

	viewZoomGroup(draw: IkeDraw): MithrilVNode {
		return m('div', { className: CSS.group }, [
			DrawButton.view(MaterialIcon.ZOOM_IN, {
				title: T.TOOL_ZOOM_IN,
				disabled: draw.state.zoom >= IkeDrawState.ZOOM_MAX,
				onclick: trackOnClick(draw, 'zoom:increase', draw.state.zoom, () => draw.state.zoomIncrease()),
			}),
			DrawButton.view(MaterialIcon.ZOOM_OUT, {
				title: T.TOOL_ZOOM_OUT,
				disabled: draw.state.zoom <= IkeDrawState.ZOOM_DEFAULT,
				onclick: trackOnClick(draw, 'zoom:decrease', draw.state.zoom, () => draw.state.zoomDecrease()),
			}),
			DrawButton.view(MaterialIcon.ZOOM_OUT_MAP, {
				title: T.TOOL_ZOOM_RESET,
				disabled: draw.state.zoom === IkeDrawState.ZOOM_DEFAULT,
				onclick: trackOnClick(draw, 'zoom:reset', draw.state.zoom, () => draw.state.zoomReset()),
			}),
			DrawButton.view(MaterialIcon.SET_CROP, {
				hidden: draw.isMarkable,
				flip: true,
				title: T.TOOL_CROP_SET,
				onclick: trackOnClick(draw, 'crop:set', draw.state.cropX, () => {
					const resolution = draw.capture.resolution;
					const center = draw.state.center;
					const zoomFactor = draw.state.zoom;
					const zoomWidth = resolution.width / zoomFactor / 2;
					const zoomHeight = resolution.height / zoomFactor / 2;

					if (zoomFactor === 1) {
						draw.state.setCrop(0, 0, 0, 0);
					} else {
						if (center.x - zoomWidth < 0) {
							center.x = zoomWidth;
						} else if (center.x + zoomWidth > resolution.width) {
							center.x = resolution.width - zoomWidth;
						}

						if (center.y - zoomHeight < 0) {
							center.y = zoomHeight;
						} else if (center.y + zoomHeight > resolution.height) {
							center.y = resolution.height - zoomHeight;
						}
						const x = center.x - zoomWidth;
						const y = center.y - zoomHeight;
						const width = zoomWidth * 2;
						const height = zoomHeight * 2;
						draw.state.setCrop(x, y, width, height);
					}
				}),
			}),
		]);
	},

	viewBrightnessGroup(draw: IkeDraw): MithrilVNode {
		return m('div', { className: CSS.group }, [
			DrawButton.view(MaterialIcon.BRIGHTNESS_HIGH, {
				title: T.TOOL_BRIGHTNESS_INCREASE,
				disabled: !draw.state.canIncreaseBrightness,
				onclick: trackOnClick(draw, 'brightness:increase', draw.state.brightness, () =>
					draw.state.brightnessIncrease()
				),
			}),
			DrawButton.view(MaterialIcon.BRIGHTNESS_MEDIUM, {
				title: T.TOOL_BRIGHTNESS_RESET,
				disabled: draw.state.brightness === IkeDrawState.BRIGHTNESS_DEFAULT,
				onclick: trackOnClick(draw, 'brightness:reset', draw.state.brightness, () => draw.state.brightnessReset()),
			}),
			DrawButton.view(MaterialIcon.BRIGHTNESS_LOW, {
				title: T.TOOL_BRIGHTNESS_DECREASE,
				disabled: !draw.state.canDecreaseBrightness,
				onclick: trackOnClick(draw, 'brightness:decrease', draw.state.brightness, () =>
					draw.state.brightnessDecrease()
				),
			}),
		]);
	},

	viewInfoGroup(draw: IkeDraw): MithrilVNode {
		return m('div', { className: CSS.group }, [
			DrawButton.view(MaterialIcon.LASER, {
				title: T.TOOL_LASER_TOGGLE,
				hidden: !draw.isIkePhoto,
				disabled: draw.capture.distance == null,
				selected: draw.state.isCrosshairVisible,
				onclick: trackOnClick(draw, `crosshair:${draw.state.isCrosshairVisible ? 'on' : 'off'}`, null, () =>
					draw.state.setCrosshair(!draw.state.isCrosshairVisible)
				),
			}),
			DrawButton.view(MaterialIcon.INFO, {
				title: T.TOOL_INFO_TOGGLE,
				disabled: !draw.isIkePhoto,
				selected: draw.state.isInfoVisible,
				onclick: trackOnClick(draw, `info:${draw.state.isInfoVisible ? 'on' : 'off'}`, null, () =>
					draw.state.setInfoVisible(!draw.state.isInfoVisible)
				),
			}),
		]);
	},

	viewResetGroup(draw: IkeDraw): MithrilVNode {
		const isPhotoHiddenAvailable = Services.Token.has(IkeToken.COLLECTION_EXPORT_HIDE_FLAGGED_PHOTOS);

		const children: MithrilVNode[] = [];

		if (isPhotoHiddenAvailable) {
			children.push(
				DrawButton.view(MaterialIcon.HIDE_PHOTO, {
					flip: true,
					title: draw.state.imageVisibilityHidden ? T.PHOTOMEASURE_UNHIDE_IMAGE_TITLE : T.PHOTOMEASURE_HIDE_IMAGE_TITLE,
					selected: draw.state.imageVisibilityHidden,
					onclick: trackOnClick(draw, 'hide:photo', null, () => draw.state.toggleImageVisibility()),
				})
			);
		}

		children.push(
			DrawButton.view(MaterialIcon.DELETE, {
				title: T.TOOL_MEASUREMENTS_CLEAR,
				disabled:
					!draw.drawState.hasMeasurements() ||
					(!draw.drawState.hasUserMeasurements() && !draw.state.visibleInsightMarkers),
				onclick: () => DrawToolbar.confirmClear(draw),
			})
		);

		return m('div', { className: CSS.group }, children);
	},

	view(draw: IkeDraw, isZoomRendererShown: boolean, ctx: SVGRenderOptions): MithrilVNode {
		const columns: MithrilVNode[] = [];

		const potentialInsightGroup = DrawToolbar.viewInsightGroup(draw, ctx);
		const potentialToolGroup = DrawToolbar.viewToolGroup(draw);
		const potentialWireGroup = DrawToolbar.viewWireGroup(draw, isZoomRendererShown);
		const potentialZoomGroup = DrawToolbar.viewZoomGroup(draw);
		const potentialBrightnessGroup = DrawToolbar.viewBrightnessGroup(draw);
		const potentialInfoGroup = DrawToolbar.viewInfoGroup(draw);
		const potentialResetGroup = DrawToolbar.viewResetGroup(draw);

		// The insight group gets its own column
		if (potentialInsightGroup) {
			columns.push(m('div', { className: CSS.column }, [potentialInsightGroup]));
		}

		const column2: MithrilVNode[] = [
			potentialToolGroup,
			potentialWireGroup,
			potentialZoomGroup,
			potentialBrightnessGroup,
			potentialInfoGroup,
			potentialResetGroup,
		].filter((x) => x != null);

		columns.push(m('div', { className: CSS.column }, column2));

		return m('div', { className: CSS.container }, columns);
	},

	async confirmClear(draw: IkeDraw) {
		const res = await ModalService.open({
			title: T.PHOTOMEASURE_DELETE_ALL_TITLE,
			content: Translate(T.PHOTOMEASURE_DELETE_ALL_TEXT),
			hasCancel: true,
		});
		if (res.ok) {
			runInAction(() => {
				draw.drawState.clear();
				draw.init();
				CurrentState.cancelInteractions();
				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.Collection,
					'measurement:clear',
					draw.collection.revision
				);
			});
		}
	},
};
